import { useMemo, useState } from "react";
import { AlertTriangle, Check, CheckCircle2, Clock3, Edit3, RotateCcw, Trash2 } from "lucide-react";
import { BlobImage } from "../components/BlobImage";
import { StatusBadge } from "../components/StatusBadge";
import {
  getIssueStatusCounts,
  hasActiveVoiceJob,
  hasPendingSpokenNote,
  isIssueOverPdfLimit
} from "../services/app/reportStatus";
import { conditionNotePath } from "../services/app/routes";
import {
  trackConditionDeleted,
  trackConditionOpened,
  trackNoteApproved
} from "../services/analytics/analyticsEvents";
import { approveIssue, deleteIssue } from "../services/db/reportRepo";
import type { Issue, Room, TranscriptionStatus } from "../services/db/schema";
import { retryIssueTranscription } from "../services/sync/transcriptionQueue";

interface ReviewRouteProps {
  rooms: Room[];
  issues: Issue[];
  onNavigate: (path: string) => void;
  onChanged: () => void;
}

interface ReviewGroup {
  title: string;
  countLabel: string;
  filter: ReviewFilter;
  statuses: readonly TranscriptionStatus[];
}

type ReviewFilter = "all" | "missing" | "review" | "ready";

const GROUPS: readonly ReviewGroup[] = [
  {
    title: "Needs Text",
    countLabel: "need text",
    filter: "missing",
    statuses: ["needs_note"]
  },
  {
    title: "Review",
    countLabel: "need review",
    filter: "review",
    statuses: ["needs_review", "audio_saved", "queued", "transcribing", "error"]
  },
  {
    title: "Ready for PDF",
    countLabel: "ready for PDF",
    filter: "ready",
    statuses: ["approved", "not_needed"]
  }
];

export function ReviewRoute({
  rooms,
  issues,
  onNavigate,
  onChanged
}: ReviewRouteProps): JSX.Element {
  const [activeFilter, setActiveFilter] = useState<ReviewFilter>("all");
  const counts = getIssueStatusCounts(issues);
  const ready = counts.complete;
  const needsAttention = counts.incomplete;
  const needsReview = counts.needsReview + counts.processing + counts.errors;
  const activeCount = getFilterCount(activeFilter, {
    all: issues.length,
    missing: counts.needsNote,
    review: needsReview,
    ready
  });
  const visibleGroups = useMemo(
    () => GROUPS.filter((group) => activeFilter === "all" || group.filter === activeFilter),
    [activeFilter]
  );

  function roomFor(issue: Issue): Room | undefined {
    return rooms.find((room) => room.id === issue.roomId);
  }

  function openIssue(issue: Issue): void {
    trackConditionOpened();
    onNavigate(conditionNotePath(issue.roomId, issue.id));
  }

  async function approve(issue: Issue): Promise<void> {
    await approveIssue(issue.id, "", issue.description ?? issue.title ?? "", false);
    trackNoteApproved((issue.description ?? issue.title ?? "").length);
    onChanged();
  }

  async function retry(issue: Issue): Promise<void> {
    await retryIssueTranscription(issue.id);
    onChanged();
  }

  async function remove(issue: Issue): Promise<void> {
    if (!window.confirm("Delete this condition note from the local draft?")) {
      return;
    }
    await deleteIssue(issue.id);
    trackConditionDeleted({ conditionCount: Math.max(0, issues.length - 1) });
    onChanged();
  }

  return (
    <section className="screen">
      <div className="topbar">
        <div className="topbar__title">
          <h1>Review Notes</h1>
          <p>
            {activeFilter === "all"
              ? `${ready} ready for PDF - ${needsAttention} need attention`
              : `${activeCount} ${getFilterSummaryLabel(activeFilter)}`}
          </p>
        </div>
      </div>

      <div className="review-summary" aria-label="Review summary">
        <button
          className="review-summary__hero"
          type="button"
          aria-pressed={activeFilter === "all"}
          onClick={() => setActiveFilter("all")}
        >
          <span>Condition notes</span>
          <strong>{issues.length}</strong>
        </button>
        <div className="review-summary__metrics">
          <ReviewMetric
            active={activeFilter === "missing"}
            tone="missing"
            icon={<AlertTriangle size={18} aria-hidden="true" />}
            label="Needs Text"
            value={counts.needsNote}
            onClick={() => setActiveFilter("missing")}
          />
          <ReviewMetric
            active={activeFilter === "review"}
            tone="review"
            icon={<Clock3 size={18} aria-hidden="true" />}
            label="Review"
            value={needsReview}
            onClick={() => setActiveFilter("review")}
          />
          <ReviewMetric
            active={activeFilter === "ready"}
            tone="ready"
            icon={<CheckCircle2 size={18} aria-hidden="true" />}
            label="Ready for PDF"
            value={ready}
            onClick={() => setActiveFilter("ready")}
          />
        </div>
      </div>

      {visibleGroups.map((group) => {
        const groupIssues = issues.filter(
          (issue) =>
            group.statuses.includes(issue.transcriptionStatus) &&
            (group.filter !== "review" || !hasActiveVoiceJob(issue))
        );
        if (groupIssues.length === 0) {
          return null;
        }
        return (
          <section className="review-section" key={group.title}>
            <div className="section-title review-section__title">
              <div>
                <h2>{group.title}</h2>
                <p className="helper">
                  {groupIssues.length} {group.countLabel}
                </p>
              </div>
            </div>
            <div className="review-list">
              {groupIssues.map((issue) => (
                <ReviewIssueCard
                  key={issue.id}
                  issue={issue}
                  room={roomFor(issue)}
                  onOpen={() => openIssue(issue)}
                  onApprove={() => void approve(issue)}
                  onRetry={() => void retry(issue)}
                  onRemove={() => void remove(issue)}
                />
              ))}
            </div>
          </section>
        );
      })}
      {(activeFilter === "all" || activeFilter === "review") && (
        <VoiceProcessingSection
          issues={issues.filter(hasActiveVoiceJob)}
          roomFor={roomFor}
          onOpen={openIssue}
          onRetry={(issue) => void retry(issue)}
          onRemove={(issue) => void remove(issue)}
        />
      )}
      {issues.length > 0 && activeCount === 0 && (
        <div className="notice">No condition notes in this filter.</div>
      )}
      {issues.length === 0 && <div className="notice">No condition notes yet. Add photos from a room.</div>}
    </section>
  );
}

function ReviewMetric({
  active,
  icon,
  label,
  onClick,
  tone,
  value
}: {
  active: boolean;
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  tone: "ready" | "review" | "missing";
  value: number;
}): JSX.Element {
  return (
    <button
      className={`review-summary__metric review-summary__metric--${tone}`}
      type="button"
      aria-pressed={active}
      onClick={onClick}
    >
      {icon}
      <strong>{value}</strong>
      <span>{label}</span>
    </button>
  );
}

function ReviewIssueCard({
  issue,
  room,
  onOpen,
  onApprove,
  onRetry,
  onRemove
}: {
  issue: Issue;
  room: Room | undefined;
  onOpen: () => void;
  onApprove: () => void;
  onRetry: () => void;
  onRemove: () => void;
}): JSX.Element {
  const note = issue.description || issue.title || "No note yet";
  const guidance = getIssueGuidance(issue);
  const primaryAction = getPrimaryAction(issue);

  return (
    <article className="review-card">
      <button className="review-card__thumb" type="button" aria-label="Open condition note" onClick={onOpen}>
        <BlobImage blobKey={issue.photoThumbBlobKey} alt="Condition note thumbnail" />
      </button>
      <div className="review-card__content">
        <div className="review-card__meta">
          <span>{room?.name ?? "Room"}</span>
          <StatusBadge status={issue.transcriptionStatus} />
        </div>
        <button className="review-card__note" type="button" onClick={onOpen}>
          {note}
        </button>
        <p>{guidance}</p>
        <div className="review-card__actions">
          {primaryAction === "add-note" && (
            <button className="button button--primary" type="button" onClick={onOpen}>
              <Edit3 size={16} aria-hidden="true" />
              Add text
            </button>
          )}
          {primaryAction === "approve" && (
            <button className="button button--primary" type="button" onClick={onApprove}>
              <Check size={16} aria-hidden="true" />
              Mark ready
            </button>
          )}
          {primaryAction === "retry" && (
            <button className="button button--primary" type="button" onClick={onRetry}>
              <RotateCcw size={16} aria-hidden="true" />
              Retry transcription
            </button>
          )}
          {primaryAction === "open" && (
            <button className="button" type="button" onClick={onOpen}>
              Open
            </button>
          )}
          {issue.transcriptionStatus !== "needs_note" && issue.transcriptionStatus !== "approved" && (
            <button className="button review-card__secondary" type="button" onClick={onOpen}>
              Edit
            </button>
          )}
        </div>
        <details className="review-card__options" data-swipe-ignore="true">
          <summary>Options</summary>
          <button className="button button--danger" type="button" onClick={onRemove}>
            <Trash2 size={16} aria-hidden="true" />
            Delete condition note
          </button>
        </details>
      </div>
    </article>
  );
}

function getPrimaryAction(issue: Issue): "add-note" | "approve" | "retry" | "open" {
  if (hasActiveVoiceJob(issue)) {
    return "open";
  }
  if (issue.transcriptionStatus === "needs_note") {
    return "add-note";
  }
  if (issue.transcriptionStatus === "needs_review") {
    if (isIssueOverPdfLimit(issue)) {
      return "open";
    }
    return issue.description || issue.title ? "approve" : "add-note";
  }
  if (issue.transcriptionStatus === "error" && issue.audioBlobKey) {
    return "retry";
  }
  return "open";
}

function getIssueGuidance(issue: Issue): string {
  switch (issue.transcriptionStatus) {
    case "needs_note":
      return "Add a short note before this can go in the PDF.";
    case "needs_review":
      if (isIssueOverPdfLimit(issue)) {
        return "Shorten this note to 260 characters before marking it ready.";
      }
      if (hasPendingSpokenNote(issue)) {
        return "A spoken note is ready. Open this condition note to use it or keep your typed note.";
      }
      return "Check the note, then mark it ready.";
    case "audio_saved":
    case "queued":
    case "transcribing":
      return "Voice note is saved locally and will become reviewable when transcription finishes. You can keep editing manually.";
    case "error":
      return issue.audioBlobKey ? "Transcription failed. Retry or type the note manually." : "Type the note manually.";
    case "approved":
    case "not_needed":
      return "Ready for PDF.";
  }
}

function VoiceProcessingSection({
  issues,
  roomFor,
  onOpen,
  onRetry,
  onRemove
}: {
  issues: Issue[];
  roomFor: (issue: Issue) => Room | undefined;
  onOpen: (issue: Issue) => void;
  onRetry: (issue: Issue) => void;
  onRemove: (issue: Issue) => void;
}): JSX.Element | null {
  if (issues.length === 0) {
    return null;
  }
  return (
    <section className="review-section">
      <div className="section-title review-section__title">
        <div>
          <h2>Voice notes processing</h2>
          <p className="helper">
            These voice notes are saved locally and will become reviewable when transcription finishes. You can keep editing manually.
          </p>
        </div>
      </div>
      <div className="review-list">
        {issues.map((issue) => (
          <ReviewIssueCard
            key={`voice-${issue.id}`}
            issue={issue}
            room={roomFor(issue)}
            onOpen={() => onOpen(issue)}
            onApprove={() => undefined}
            onRetry={() => onRetry(issue)}
            onRemove={() => onRemove(issue)}
          />
        ))}
      </div>
    </section>
  );
}

function getFilterCount(
  filter: ReviewFilter,
  counts: Record<ReviewFilter, number>
): number {
  return counts[filter];
}

function getFilterSummaryLabel(filter: ReviewFilter): string {
  switch (filter) {
    case "missing":
      return "need text";
    case "review":
      return "need review";
    case "ready":
      return "ready for PDF";
    case "all":
      return "condition notes";
  }
}
